import type { Task, TaskNode } from './types';

/**
 * Build a nested tree from the flat, path-ordered list the API returns.
 *
 * The server already sorts by ltree path, so a parent always appears before
 * its children and one pass is enough.
 */
export function buildTree(tasks: Task[]): TaskNode[] {
  const byId = new Map<number, TaskNode>();
  const roots: TaskNode[] = [];

  for (const task of tasks) {
    byId.set(task.id, { ...task, children: [] });
  }

  for (const task of tasks) {
    const node = byId.get(task.id)!;
    const parent = task.parent_task_id ? byId.get(task.parent_task_id) : undefined;

    // A task whose parent is filtered out of this result set is rendered at
    // the top level rather than dropped -- silently hiding work is the one
    // thing a backlog must never do.
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  return roots;
}

/**
 * Ids of `taskId` and everything beneath it, from a flat list.
 *
 * The set a task cannot be moved into: re-parenting a task beneath its own
 * descendant detaches the whole branch from the tree, so those ids are the
 * ones a parent picker has to leave out.
 */
export function subtreeIds(taskId: number, tasks: Task[]): Set<number> {
  const childrenOf = new Map<number, number[]>();
  for (const task of tasks) {
    if (task.parent_task_id == null) continue;
    const bucket = childrenOf.get(task.parent_task_id);
    if (bucket) bucket.push(task.id);
    else childrenOf.set(task.parent_task_id, [task.id]);
  }

  const ids = new Set<number>([taskId]);
  const queue = [taskId];
  while (queue.length > 0) {
    for (const child of childrenOf.get(queue.pop()!) ?? []) {
      // Guarding against a cycle the server should never produce, because the
      // alternative is an infinite loop in the browser.
      if (ids.has(child)) continue;
      ids.add(child);
      queue.push(child);
    }
  }
  return ids;
}

/** `95` -> `1h 35m`. Compact because it appears inline, many times per screen. */
export function formatDuration(minutes: number | null): string | null {
  if (minutes === null || minutes <= 0) return null;

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}
